// page-meeting-actions-tab.ts — Onglets « Principales actions », « Support »
// et « Indicateurs » de la fiche réunion : liens sortants et chargements.
//
// Extrait de `renderMeetingPage` le 2026-09-08 (point 16). Les liens sont
// purs (testables) ; les chargeurs écrivent dans leur hôte DOM.
//
// Globaux ambiants : esc, t, api, currentUserId, window.componentActionRow.

import type { MeetingPageState } from '../domain/meeting-form.js';
import { consumeLastModifiedAnimationMany } from '../utils/last-modified-anim.js';
import { renderIndicatorsContent, type MpActionStats } from './page-meeting-indicators.js';

interface ActionLite {
  id: number;
  [k: string]: unknown;
}

/** Paramètres du périmètre de la réunion (programme, projet, comité, structure). */
function scopeParams(state: MeetingPageState, structureKey: 'org' | 'struct'): URLSearchParams {
  const params = new URLSearchParams();
  if (state.programId) params.set('prog', String(state.programId));
  if (state.projectId) params.set('proj', String(state.projectId));
  if (state.committeeId) params.set('com', String(state.committeeId));
  if (state.structureId) params.set(structureKey, String(state.structureId));
  return params;
}

/** Périmètre de la réunion transmis à #actions, sur toutes les actions. */
function actionsHash(state: MeetingPageState, extra: Record<string, string>): string {
  const params = scopeParams(state, 'org');
  for (const [k, v] of Object.entries(extra)) params.set(k, v);
  params.set('all', '1');
  params.set('scope', '1');
  return '#actions?' + params.toString();
}

/** Mode rattachement de #actions : la page prend la main puis revient ici. */
export function linkActionsHash(meetingId: number, state: MeetingPageState): string {
  return actionsHash(state, { meeting: String(meetingId) });
}

/** Mode consultation : les actions liées pré-cochées, sans bandeau, et le
 * filtre « afficher la sélection » pour n'avoir qu'elles sous les yeux. */
export function viewActionsHash(meetingId: number, state: MeetingPageState): string {
  return actionsHash(state, { meeting_view: String(meetingId), selected: '1' });
}

/** Wizard #meeting-support pré-rempli : saute l'écran d'intro. */
export function supportWizardHash(state: MeetingPageState): string {
  const params = scopeParams(state, 'struct');
  if (state.withRisks) params.set('risks', '1');
  return '#meeting-support?' + params.toString();
}

/** Hash de retour du wizard, lu par page-meeting-support à la fin. */
export function rememberSupportBackHash(meetingId: number): void {
  try {
    sessionStorage.setItem('meetingSupport:backHash', `#meeting/${meetingId}?tab=support`);
  } catch {
    /* sessionStorage indisponible : le wizard reste sur #meeting-support */
  }
}

/**
 * Table des actions liées, en lecture seule, dans `#mp-actions-host`. Demande
 * directement les actions de la réunion (perf #9) plutôt que toutes celles
 * du tenant. Anime les ajouts au retour du mode rattachement.
 */
export async function loadActionsTab(
  meetingId: number,
  users: ReadonlyArray<{ id: number }>,
  projects: ReadonlyArray<{ id: number }>,
): Promise<void> {
  const host = document.getElementById('mp-actions-host');
  if (!host) return;
  try {
    const [linked, statuses] = (await Promise.all([
      api.getMeetingActions(meetingId, true),
      api.getActionStatuses(),
    ])) as [{ action_ids: number[]; actions?: ActionLite[] }, unknown[]];
    const actions = linked.actions || [];
    if (!actions.length) {
      host.innerHTML = `<div class="empty-state" style="padding:24px;text-align:center;color:var(--text3)">${esc(t('pageMeeting.actionsEmpty'))}</div>`;
      return;
    }
    const opts = {
      statuses,
      userMap: Object.fromEntries(users.map((u) => [u.id, u])),
      projectMap: Object.fromEntries(projects.map((p) => [p.id, p])),
      readOnly: true,
      currentUserId: currentUserId(),
      userTeamIds: [],
    };
    const headersHtml = window.componentActionRow.buildActionHeadersHtml();
    const rowsHtml = actions
      .map((a) => window.componentActionRow.buildActionRowHtml(a as never, opts as never))
      .join('');
    host.innerHTML = `<div class="table-wrap"><table class="table">${headersHtml}<tbody>${rowsHtml}</tbody></table></div>`;
    consumeLastModifiedAnimationMany('meeting-actions', (id) =>
      host.querySelector<HTMLElement>(`tr[data-action-id="${id}"]`),
    );
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : t('common.error');
    host.innerHTML = `<div class="error">${esc(msg)}</div>`;
  }
}

/**
 * Indicateurs dans `#mp-indicators-host`. Palier insuffisant : message
 * verrou sans appel API (l'endpoint répondrait 403 ; l'onglet est déjà
 * cadenassé dans la barre).
 */
export async function loadIndicatorsTab(meetingId: number, allowed: boolean): Promise<void> {
  const host = document.getElementById('mp-indicators-host');
  if (!host) return;
  if (!allowed) {
    host.innerHTML = `<div class="empty-state" style="padding:24px;text-align:center;color:var(--text3)">${esc(t('pageMeeting.indicators.locked'))}</div>`;
    return;
  }
  try {
    const stats = (await api.getMeetingActionStats(meetingId)) as MpActionStats;
    host.innerHTML = renderIndicatorsContent(stats);
  } catch {
    host.innerHTML = `<div class="error" style="color:var(--danger)">${esc(t('common.error'))}</div>`;
  }
}
